/**
 * Shared dashboard timeline and project filter semantics.
 *
 * Single source of truth for date windows across Overview, Executive, Financial,
 * Analytics, and Resource Planning dashboards. Aligns with
 * `.cursor/plans/canonical_kpi_spec_completion_tdd_plan_468c9bd4.plan.md`.
 */

import { convertTimeRangeToDates } from '../hooks/useAnalytics';

export const DASHBOARD_SCOPE_STORAGE_KEY = 'psa-dashboard-scope';

/** Default rolling window — matches Executive / Financial (90 days). */
export const DEFAULT_DASHBOARD_TIME_RANGE = '90d';

export const DEFAULT_DASHBOARD_PROJECT_ID = 'all';

export const DEFAULT_DASHBOARD_PROJECT_STATUS = 'all';

/** Project.status values aligned with projects.models.Project.STATUS_CHOICES. */
export const DASHBOARD_PROJECT_STATUS_OPTIONS = [
    { value: 'all', label: 'All statuses' },
    { value: 'intake', label: 'Intake' },
    { value: 'planning', label: 'Planning' },
    { value: 'active', label: 'Active' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'archived', label: 'Archived' },
];

export const TIMELINE_PRESET_TO_SCOPE = {
    '7': '7d',
    '30': '30d',
    '90': '90d',
    '180': '180d',
    '365': '365d',
    '1y': '1y',
    ytd: 'ytd',
};

/** Rolling-window presets for the global DashboardScopeStrip (Option A). */
export const DASHBOARD_SCOPE_PRESET_OPTIONS = [
    { value: '7d', label: '7 days' },
    { value: '30d', label: '30 days' },
    { value: '90d', label: '90 days' },
    { value: '180d', label: '180 days' },
    { value: '1y', label: '1 year' },
    { value: 'ytd', label: 'YTD' },
];

export const DASHBOARD_SCOPE_ROUTE_PREFIXES = [
    '/executive-dashboard',
    '/financial-management',
    '/overview-dashboard',
    '/analytics-dashboard',
    '/advanced-analytics',
    '/resource-planning',
    '/ml-dashboard',
];

export const CUSTOM_DASHBOARD_SCOPE_MIN_DAYS = 1;
export const CUSTOM_DASHBOARD_SCOPE_MAX_DAYS = 730;

export const DASHBOARD_PERIOD_LABELS = {
    '7d': 'Last 7 Days',
    '30d': 'Last 30 Days',
    '90d': 'Last 90 Days',
    '180d': 'Last 180 Days',
    '365d': 'Last 365 Days',
    '1y': 'Last Year',
    ytd: 'Year to Date',
};

const PRESET_TIME_RANGE_VALUES = new Set(
    DASHBOARD_SCOPE_PRESET_OPTIONS.map((option) => option.value)
);

/**
 * Map Unified PSA timeline preset (e.g. "90") to scope time_range (e.g. "90d").
 *
 * @param {string} preset - Executive dashboard preset value
 * @returns {string} Scope time_range key
 */
export const timelinePresetToScope = (preset) => {
    if (!preset) {
        return DEFAULT_DASHBOARD_TIME_RANGE;
    }
    if (preset === 'ytd') {
        return 'ytd';
    }
    const mapped = TIMELINE_PRESET_TO_SCOPE[`${preset}`];
    if (mapped) {
        return mapped;
    }
    const numeric = Number.parseInt(`${preset}`, 10);
    if (Number.isFinite(numeric) && numeric > 0) {
        return `${numeric}d`;
    }
    return DEFAULT_DASHBOARD_TIME_RANGE;
};

/**
 * Map scope time_range to Executive timeline preset.
 *
 * @param {string} timeRange - Scope time_range (e.g. "90d")
 * @returns {string|null} Timeline preset for UnifiedPSADashboard, or null when unset
 */
export const scopeToTimelinePreset = (timeRange) => {
    if (!timeRange) {
        // Never invent a rolling default from empty input (semantic period mode).
        return null;
    }
    if (timeRange === 'ytd') {
        return 'ytd';
    }
    if (timeRange === '1y') {
        return '365';
    }
    const match = typeof timeRange === 'string' ? /^(\d+)d$/.exec(timeRange.trim()) : null;
    if (match) {
        return match[1];
    }
    return '90';
};

/**
 * @param {string} timeRange - Scope time_range key
 * @returns {{ start_date: string, end_date: string }}
 */
export const resolveDashboardDateRange = (timeRange) => (
    convertTimeRangeToDates(timeRange || DEFAULT_DASHBOARD_TIME_RANGE)
);

/**
 * Human-readable label for a scope time_range key.
 *
 * @param {string} timeRange - e.g. "90d", "120d", "ytd"
 * @returns {string} Period label, or empty string when timeRange is unset
 */
export const getDashboardPeriodLabel = (timeRange) => {
    if (!timeRange) {
        // Never invent "Last 90 Days" for empty input — semantic period modes
        // must fall through to mode-aware labels instead.
        return '';
    }
    if (DASHBOARD_PERIOD_LABELS[timeRange]) {
        return DASHBOARD_PERIOD_LABELS[timeRange];
    }
    const match = typeof timeRange === 'string' ? /^(\d+)d$/.exec(timeRange.trim()) : null;
    if (match) {
        return `Last ${match[1]} Days`;
    }
    return timeRange;
};

/**
 * @param {{ start_date?: string, end_date?: string }} dateRange
 * @returns {string}
 */
export const formatDashboardDateRangeLabel = (dateRange) => {
    const { start_date: startDate, end_date: endDate } = dateRange || {};
    if (!startDate || !endDate) {
        return '';
    }
    const formatPart = (isoDate) => new Date(`${isoDate}T12:00:00`).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
    return `${formatPart(startDate)} – ${formatPart(endDate)}`;
};

/**
 * ISO week start (Monday) for a calendar date string.
 *
 * @param {string|null|undefined} isoDate - YYYY-MM-DD date.
 * @returns {string|null}
 */
export const getIsoWeekStartMonday = (isoDate) => {
    if (!isoDate) {
        return null;
    }

    const date = new Date(`${isoDate}T12:00:00`);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const day = date.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + diffToMonday);
    return date.toISOString().slice(0, 10);
};

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export const isDashboardScopeRoute = (pathname) => (
    DASHBOARD_SCOPE_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix))
);

/**
 * Parse rolling-day count from an Nd scope key.
 *
 * @param {string} timeRange
 * @returns {number|null}
 */
export const parseRollingDaysFromTimeRange = (timeRange) => {
    const match = typeof timeRange === 'string' ? /^(\d+)d$/.exec(timeRange.trim()) : null;
    if (!match) {
        return null;
    }
    const days = Number.parseInt(match[1], 10);
    return Number.isFinite(days) && days > 0 ? days : null;
};

/**
 * @param {string} timeRange
 * @returns {boolean}
 */
export const isPresetDashboardTimeRange = (timeRange) => PRESET_TIME_RANGE_VALUES.has(timeRange);

/**
 * Clamp custom rolling-window days for DashboardScopeStrip.
 *
 * @param {number|string} rawDays
 * @returns {number}
 */
/**
 * @param {string} projectStatus
 * @returns {string}
 */
export const getDashboardProjectStatusLabel = (projectStatus) => {
    const match = DASHBOARD_PROJECT_STATUS_OPTIONS.find(
        (option) => option.value === projectStatus
    );
    return match ? match.label : projectStatus;
};

/**
 * Build API query params for global dashboard scope (period + portfolio).
 *
 * @param {{
 *   dateRange?: { start_date?: string, end_date?: string },
 *   projectId?: string,
 *   projectStatus?: string
 * }} scope
 * @returns {Record<string, string>}
 */
export const buildDashboardScopeApiParams = ({
    dateRange,
    projectId = DEFAULT_DASHBOARD_PROJECT_ID,
    projectStatus = DEFAULT_DASHBOARD_PROJECT_STATUS,
} = {}) => {
    const params = {};
    if (dateRange?.start_date) {
        params.start_date = dateRange.start_date;
    }
    if (dateRange?.end_date) {
        params.end_date = dateRange.end_date;
    }
    if (projectId && projectId !== DEFAULT_DASHBOARD_PROJECT_ID) {
        params.project_id = `${projectId}`;
    } else if (projectStatus && projectStatus !== DEFAULT_DASHBOARD_PROJECT_STATUS) {
        params.status = projectStatus;
    }
    return params;
};

/**
 * @returns {boolean}
 */
export const hasActivePortfolioScopeFilters = (
    projectId = DEFAULT_DASHBOARD_PROJECT_ID,
    projectStatus = DEFAULT_DASHBOARD_PROJECT_STATUS
) => (
    (projectId && projectId !== DEFAULT_DASHBOARD_PROJECT_ID)
    || (projectStatus && projectStatus !== DEFAULT_DASHBOARD_PROJECT_STATUS)
);

export const normalizeCustomScopeDays = (rawDays) => {
    const parsed = Number.parseInt(`${rawDays}`, 10);
    if (!Number.isFinite(parsed)) {
        return DEFAULT_DASHBOARD_TIME_RANGE === 'ytd'
            ? 90
            : parseRollingDaysFromTimeRange(DEFAULT_DASHBOARD_TIME_RANGE) || 90;
    }
    return Math.min(
        CUSTOM_DASHBOARD_SCOPE_MAX_DAYS,
        Math.max(CUSTOM_DASHBOARD_SCOPE_MIN_DAYS, parsed)
    );
};

/**
 * Load persisted scope from sessionStorage.
 *
 * @returns {{ timeRange: string, projectId: string, projectStatus: string }}
 */
export const loadStoredDashboardScope = () => {
    if (typeof window === 'undefined') {
        return {
            timeRange: DEFAULT_DASHBOARD_TIME_RANGE,
            projectId: DEFAULT_DASHBOARD_PROJECT_ID,
            projectStatus: DEFAULT_DASHBOARD_PROJECT_STATUS,
        };
    }

    try {
        const raw = window.sessionStorage.getItem(DASHBOARD_SCOPE_STORAGE_KEY);
        if (!raw) {
            return {
                timeRange: DEFAULT_DASHBOARD_TIME_RANGE,
                projectId: DEFAULT_DASHBOARD_PROJECT_ID,
                projectStatus: DEFAULT_DASHBOARD_PROJECT_STATUS,
            };
        }
        const parsed = JSON.parse(raw);
        return {
            timeRange: typeof parsed.timeRange === 'string'
                ? parsed.timeRange
                : DEFAULT_DASHBOARD_TIME_RANGE,
            projectId: parsed.projectId != null
                ? `${parsed.projectId}`
                : DEFAULT_DASHBOARD_PROJECT_ID,
            projectStatus: typeof parsed.projectStatus === 'string'
                ? parsed.projectStatus
                : DEFAULT_DASHBOARD_PROJECT_STATUS,
        };
    } catch {
        return {
            timeRange: DEFAULT_DASHBOARD_TIME_RANGE,
            projectId: DEFAULT_DASHBOARD_PROJECT_ID,
            projectStatus: DEFAULT_DASHBOARD_PROJECT_STATUS,
        };
    }
};

/**
 * Persist scope to sessionStorage.
 *
 * @param {{ timeRange: string, projectId: string, projectStatus?: string }} scope
 */
export const saveDashboardScope = (scope) => {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.sessionStorage.setItem(
            DASHBOARD_SCOPE_STORAGE_KEY,
            JSON.stringify({
                timeRange: scope.timeRange || DEFAULT_DASHBOARD_TIME_RANGE,
                projectId: scope.projectId != null
                    ? `${scope.projectId}`
                    : DEFAULT_DASHBOARD_PROJECT_ID,
                projectStatus: scope.projectStatus || DEFAULT_DASHBOARD_PROJECT_STATUS,
            })
        );
    } catch {
        // sessionStorage may be unavailable in private mode
    }
};
